import { VoucherCSVRow, CSV_HEADERS } from "../models/Vouchers/VoucherEmailer";

export interface VoucherCsvValidationError {
  row: number;
  field: string;
  message: string;
}

const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validateVoucherRow = (
  rowData: Record<string, string>,
  rowNumber: number,
): VoucherCsvValidationError[] => {
  const errors: VoucherCsvValidationError[] = [];

  if (!rowData.Name || rowData.Name.trim() === "") {
    errors.push({
      row: rowNumber,
      field: "Name",
      message: "Name is required",
    });
  }

  if (!rowData.Email || rowData.Email.trim() === "") {
    errors.push({
      row: rowNumber,
      field: "Email",
      message: "Email is required",
    });
  } else if (!validateEmail(rowData.Email.trim())) {
    errors.push({
      row: rowNumber,
      field: "Email",
      message: "Invalid email format",
    });
  }

  if (!rowData["Product Name"] || rowData["Product Name"].trim() === "") {
    errors.push({
      row: rowNumber,
      field: "Product Name",
      message: "Product Name is required",
    });
  }

  if (!rowData.Amount || rowData.Amount.trim() === "") {
    errors.push({
      row: rowNumber,
      field: "Amount",
      message: "Amount is required",
    });
  } else if (isNaN(parseFloat(rowData.Amount))) {
    errors.push({
      row: rowNumber,
      field: "Amount",
      message: "Amount must be a number",
    });
  }

  if (!rowData["Reference ID"] || rowData["Reference ID"].trim() === "") {
    errors.push({
      row: rowNumber,
      field: "Reference ID",
      message: "Reference ID is required",
    });
  }

  if (!rowData["Expiry Date"] || rowData["Expiry Date"].trim() === "") {
    errors.push({
      row: rowNumber,
      field: "Expiry Date",
      message: "Expiry Date is required",
    });
  } else if (isNaN(Date.parse(rowData["Expiry Date"]))) {
    errors.push({
      row: rowNumber,
      field: "Expiry Date",
      message: "Expiry Date must be a valid date",
    });
  }

  if (!rowData["Gift Card Code"] || rowData["Gift Card Code"].trim() === "") {
    errors.push({
      row: rowNumber,
      field: "Gift Card Code",
      message: "Gift Card Code is required",
    });
  }

  if (!rowData["Currency Code"] || rowData["Currency Code"].trim() === "") {
    errors.push({
      row: rowNumber,
      field: "Currency Code",
      message: "Currency Code is required",
    });
  }

  return errors;
};

export const downloadVoucherCsvTemplate = () => {
  const csvContent = CSV_HEADERS.join(",") + "\n";
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", "voucher_emailer_template.csv");
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const detectDelimiter = (firstLine: string): "," | "\t" => {
  const commaCount = (firstLine.match(/,/g) || []).length;
  const tabCount = (firstLine.match(/\t/g) || []).length;
  return tabCount > commaCount ? "\t" : ",";
};

const parseCsvLine = (line: string, delimiter: "," | "\t"): string[] => {
  if (delimiter === "\t") {
    return line.split("\t").map((cell) => cell.trim());
  }

  const result: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }

  result.push(current.trim());
  return result;
};

const isRowEmpty = (row: string[]): boolean => {
  return row.every((cell) => cell.trim() === "");
};

export const parseVoucherCsv = (
  csvText: string,
): {
  data: VoucherCSVRow[];
  errors: VoucherCsvValidationError[];
} => {
  const lines = csvText.split("\n").filter((line) => line.trim() !== "");

  if (lines.length === 0) {
    return {
      data: [],
      errors: [{ row: 0, field: "CSV", message: "CSV file is empty" }],
    };
  }

  const firstLine = lines[0];
  const delimiter = detectDelimiter(firstLine);
  const headers = parseCsvLine(firstLine, delimiter);

  const expectedHeaders = CSV_HEADERS;
  const headerMismatch = expectedHeaders.some(
    (header, index) => headers[index] !== header,
  );

  if (headerMismatch) {
    return {
      data: [],
      errors: [
        {
          row: 0,
          field: "Headers",
          message: `Invalid CSV headers. Expected: ${expectedHeaders.join(", ")}`,
        },
      ],
    };
  }

  const data: VoucherCSVRow[] = [];
  const errors: VoucherCsvValidationError[] = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    const cells = parseCsvLine(line, delimiter);

    if (isRowEmpty(cells)) {
      continue;
    }

    const rowData: Record<string, string> = {};
    headers.forEach((header, index) => {
      rowData[header] = cells[index] || "";
    });

    const rowErrors = validateVoucherRow(rowData, i + 1);

    if (rowErrors.length > 0) {
      errors.push(...rowErrors);
    } else {
      data.push(rowData as unknown as VoucherCSVRow);
    }
  }

  return { data, errors };
};

export const formatVoucherCsvErrors = (
  errors: VoucherCsvValidationError[],
  maxErrors: number = 5,
): string[] => {
  const limitedErrors = errors.slice(0, maxErrors);
  const formatted = limitedErrors.map(
    (error) => `Row ${error.row}: ${error.field} - ${error.message}`,
  );

  if (errors.length > maxErrors) {
    formatted.push(`... and ${errors.length - maxErrors} more errors`);
  }

  return formatted;
};
